import React, { useEffect, useRef, useState } from 'react';
import { Property, RoomType } from '@lib/state';
import { useLookup, LoadingIndicator, DataTable } from '@lib/common';
import { AdditionalProperty, RatePlanProperty } from 'app/state';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { DSOFeeOverrideColumn, supportsDoubleOccupancy } from './dso-fee-column';
import { MealPlanFeeColumn } from './meal-plan-fee-column';
import { MealPlanAvailabilityColumn } from './meal-plan-availability-column';
import { NumberCellInput } from './number-cell-input';
import { getSelectedPropertyIds, PropertyRow } from './property-rows';
import { applyOverrideToSelection, toAdditionalProperty } from './selected-rates';

type RateOverride = {
  standardRate?: number;
  dsoFee?: number;
  mealPlanFee?: number;
  mealPlanAvailable?: boolean;
};

interface Props {
  rows: Array<PropertyRow>;
  roomTypes: Array<RoomType>;
  properties: Array<Property>;
  setSelectedRates: React.Dispatch<React.SetStateAction<AdditionalProperty[]>>;
  selectedRates: AdditionalProperty[];
  setPropertySelected: React.Dispatch<React.SetStateAction<boolean>>;
  mainRoomTypeId?: string;
  mainPlanStandardRate: number;
  mainPlanDsoFee: number;
  mainPlanMealPlanFee?: number;
  mealPlanEnabled: boolean;
  // the DSO Fee override is offered only when the selected room type is double occupancy.
  showDsoFee: boolean;
  onValidityChange: (isValid: boolean) => void;
}

// Create only: lists the other properties that can receive this plan. Ticking a property creates a new
// plan there from the main plan's values; any amount staff change is applied to that property only.
export const PropertyOverrideList: React.FC<Props> = ({
  rows,
  roomTypes,
  properties,
  setSelectedRates,
  selectedRates,
  setPropertySelected,
  mainRoomTypeId,
  mainPlanStandardRate,
  mainPlanDsoFee,
  mainPlanMealPlanFee,
  mealPlanEnabled,
  showDsoFee,
  onValidityChange,
}) => {
  const roomTypeLookup = useLookup(roomTypes, x => x.id);
  const propertyLookup = useLookup(properties, x => x.id);

  const items = rows
    .map(
      r =>
        ({
          ...r,
          propertyName: propertyLookup.get(r.propertyId)?.name,
        } as RatePlanProperty)
    )
    .sort((a, b) => (a.propertyName ?? '').localeCompare(b.propertyName ?? ''));

  // per-property overrides keyed by property id; only entered values are sent, the rest follow the main plan.
  const [overrides, setOverrides] = useState<Record<string, RateOverride>>({});

  // nothing is persisted here, so start over when the room type or the set of listed properties changes.
  const rowsSignature = rows
    .map(r => r.propertyId)
    .sort()
    .join(',');
  const resetKey = `${mainRoomTypeId ?? ''}|${rowsSignature}`;
  const resetForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (resetForKeyRef.current === resetKey) return;
    resetForKeyRef.current = resetKey;

    setOverrides({});
    setSelectedRates([]);
    setPropertySelected(false);
    // eslint-disable-next-line
  }, [resetKey]);

  // amounts follow the main plan's validation (no negatives), but only for ticked rows: only those are saved.
  useEffect(() => {
    const selectedKeys = new Set(selectedRates.map(rate => rate.propertyId));
    const isInvalid = Object.entries(overrides).some(
      ([key, o]) =>
        selectedKeys.has(key) &&
        ((o.standardRate ?? 0) < 0 || (o.dsoFee ?? 0) < 0 || (o.mealPlanFee ?? 0) < 0)
    );
    onValidityChange(!isInvalid);
  }, [overrides, selectedRates, onValidityChange]);

  const handleOverrideChange = <K extends keyof RateOverride>(
    item: RatePlanProperty,
    field: K,
    value: RateOverride[K]
  ) => {
    const key = item.propertyId;
    const next = { ...overrides[key], [field]: value };
    setOverrides(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

    setSelectedRates(prev =>
      applyOverrideToSelection(prev, toAdditionalProperty(item, next), field, value)
    );
    if (value !== undefined) setPropertySelected(true);
  };

  const getMealPlanAvailable = (item: RatePlanProperty) =>
    overrides[item.propertyId]?.mealPlanAvailable ?? mealPlanEnabled;

  if (!roomTypes.length) return <LoadingIndicator />;

  return (
    <DataTable
      key={resetKey}
      items={items}
      checkboxSelection={true}
      getItemKey={item => item.propertyId}
      onSelectionModelChange={(selectedRows: GridSelectionModel) => {
        setSelectedRates(
          selectedRows.map(x => {
            const item = items.find(i => i.propertyId === x)!;
            return toAdditionalProperty(item, overrides[item.propertyId]);
          })
        );
        setPropertySelected(selectedRows.length > 0);
      }}
      selectionModel={getSelectedPropertyIds(selectedRates, rows)}
      columns={[
        {
          title: 'Property',
          valueFactory: plan => plan.propertyName,
        },
        {
          title: 'Room',
          valueFactory: plan => roomTypeLookup.get(plan.roomTypeId)?.name,
        },
        {
          title: 'Standard Rate',
          align: 'center',
          valueFactory: (item: RatePlanProperty) => {
            const value = overrides[item.propertyId]?.standardRate ?? mainPlanStandardRate;
            return (
              <NumberCellInput
                value={value}
                onChange={value => handleOverrideChange(item, 'standardRate', value)}
                error={value < 0}
                helperText={value < 0 ? 'Value cannot be less than zero' : undefined}
                aria-label={`Standard rate for ${item.propertyName}`}
                data-testid="ratePlanRowStandardRateInput"
              />
            );
          },
          colSpan: 1.3,
        },
        ...MealPlanAvailabilityColumn(
          plan => getMealPlanAvailable(plan as RatePlanProperty),
          (plan, available) =>
            handleOverrideChange(plan as RatePlanProperty, 'mealPlanAvailable', available)
        ),
        ...MealPlanFeeColumn(
          plan => getMealPlanAvailable(plan as RatePlanProperty),
          plan =>
            overrides[(plan as RatePlanProperty).propertyId]?.mealPlanFee ??
            mainPlanMealPlanFee ??
            0,
          (plan, value) => handleOverrideChange(plan as RatePlanProperty, 'mealPlanFee', value)
        ),
        ...(showDsoFee
          ? DSOFeeOverrideColumn(
              plan => supportsDoubleOccupancy(roomTypeLookup.get(plan.roomTypeId)),
              plan => overrides[(plan as RatePlanProperty).propertyId]?.dsoFee ?? mainPlanDsoFee,
              (plan, value) => handleOverrideChange(plan as RatePlanProperty, 'dsoFee', value)
            )
          : []),
      ]}
    />
  );
};
